import React, { memo, useCallback, useMemo } from 'react';

import { Avatar, Box, Button, Typography } from '@mui/material';
import CheckboxCDS from '@sharedCR/coreDesignSystem/form/CheckboxCDS';
import PropTypes from 'prop-types';

import { SELECT_ALL_LABEL, SHOW_ON_LOAN_TITLE } from './PendingChangesAuthorsMenu.constants';
import {
  authorRowStyles,
  authorTextStyles,
  authorTitleStyles,
  getAuthorAvatarStyles,
  getAuthorDotStyles,
  menuSectionStyles,
  sectionHeaderStyles,
  selectAllButtonStyles,
  truncatedTextStyles,
} from './PendingChangesAuthorsMenu.styles';
import { PendingChangesAuthorsMenuTestIds } from './PendingChangesAuthorsMenu.testIds';
import { buildAuthorMenuRows } from './PendingChangesAuthorsMenu.utils';

const PendingChangesAuthorsMenu = ({
  authors,
  selectedAuthorKeys,
  isDisabled = false,
  onToggleAuthor,
  onSelectAll,
}) => {
  const rows = useMemo(
    () => buildAuthorMenuRows({ authors, selectedAuthorKeys }),
    [authors, selectedAuthorKeys],
  );

  const onRowClick = useCallback(
    event => {
      if (isDisabled) {
        return;
      }

      onToggleAuthor(event.currentTarget.dataset.authorKey);
    },
    [isDisabled, onToggleAuthor],
  );

  return (
    <Box sx={menuSectionStyles}>
      <Box sx={sectionHeaderStyles}>
        <Typography color="text.secondary" variant="medium12">
          {SHOW_ON_LOAN_TITLE}
        </Typography>
        <Button
          data-testid={PendingChangesAuthorsMenuTestIds.SELECT_ALL_BUTTON}
          disabled={isDisabled}
          size="small"
          sx={selectAllButtonStyles}
          variant="text"
          onClick={onSelectAll}
        >
          {SELECT_ALL_LABEL}
        </Button>
      </Box>
      {rows.map(row => (
        <Box data-author-key={row.key} key={row.key} sx={authorRowStyles} onClick={onRowClick}>
          <CheckboxCDS
            dataTestId={PendingChangesAuthorsMenuTestIds.getAuthorCheckboxId(row.key)}
            disabled={isDisabled}
            value={row.isSelected}
          />
          <Avatar sx={getAuthorAvatarStyles(row.color)}>{row.initials}</Avatar>
          <Box sx={authorTextStyles}>
            <Box sx={authorTitleStyles}>
              <Box component="span" sx={getAuthorDotStyles(row.color)} />
              <Typography sx={truncatedTextStyles} variant="medium14">
                {row.title}
              </Typography>
              {!!row.role && (
                <Typography color="text.secondary" variant="regular12">
                  {row.role}
                </Typography>
              )}
            </Box>
            <Typography color="text.secondary" sx={truncatedTextStyles} variant="regular12">
              {row.subtitle}
            </Typography>
          </Box>
          <Typography color="text.secondary" variant="medium12">
            {row.changesCount}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

PendingChangesAuthorsMenu.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      role: PropTypes.string,
      color: PropTypes.string,
      changesCount: PropTypes.number,
      isCurrentUser: PropTypes.bool,
      isAi: PropTypes.bool,
    }),
  ).isRequired,
  selectedAuthorKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleAuthor: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

export default memo(PendingChangesAuthorsMenu);
